import type { GUID } from "#public"

import { GUIDToUint8 } from "../encoding/GUIDToUint8"
import { UInt8Hash } from "./UInt8Hash"

/**
 * Computes HMAC-SHA256 message authentication code.
 *
 * Implements RFC 2104 HMAC with SHA-256 hash function.
 *
 * @param key - Secret key as Uint8Array
 * @param message - Message to authenticate as Uint8Array
 * @returns Promise resolving to GUID hash
 *
 * @example
 * ```ts
 * import { HMACSha256 } from "@pdfking/tfunc"
 *
 * HMACSha256(new Uint8Array([1, 2, 3]), new Uint8Array([4, 5, 6]))
 * ```
 */
export const HMACSha256 = (key: Uint8Array, message: Uint8Array): GUID => {
  const blockSize = 64 // SHA-256 block size

  // If key is longer than block size, hash it first
  let processedKey = key
  if (key.length > blockSize) {
    processedKey = GUIDToUint8(UInt8Hash([key]))
  }

  // Pad key to block size
  const paddedKey = new Uint8Array(blockSize)
  paddedKey.set(processedKey)

  // Create inner and outer padding
  const ipad = new Uint8Array(blockSize)
  const opad = new Uint8Array(blockSize)

  for (let i = 0; i < blockSize; i++) {
    ipad[i] = paddedKey[i] ^ 0x36
    opad[i] = paddedKey[i] ^ 0x5c
  }

  // Inner hash: hash(ipad || message)
  const innerHashBytes = GUIDToUint8(UInt8Hash([ipad, message]))

  // Outer hash: hash(opad || innerHash)
  return UInt8Hash([opad, innerHashBytes])
}
